import * as Yup from "yup";
import { States } from "../../../../../constants";
import { emailRegEx, phoneRegEx, zipCodeRegEx } from "utils/RegularExpressions";
import { userDocumentsInfos } from "./Prerequisities/constant";
import { initialValues } from "./constants";

interface TestContextExtended {
  from: {
    value: typeof initialValues;
  }[];
}

export const validStateOptions: string[] = States.map((state) => state.name);
const validOptions: string[] = userDocumentsInfos.map((doc) => doc.value);

export const userDocumentInfoSchema = Yup.object({
  userDocumentInfo: Yup.array()
    .required("Required")
    .typeError("Required")
    .of(Yup.string().oneOf(validOptions))
    .test("options_validation", "Invalid options selected", (val) => {
      if (val?.includes("hasNothing") && val.length > 1) {
        return false;
      }
      return true;
    }),
});

export const defaultStepsSchema = Yup.object({
  applicantInformation: Yup.object({
    fullName: Yup.string()
      .required("Required")
      .max(250, "Must be less than 250 characters."),
    contactEmail: Yup.string()
      .required("Required")
      .matches(emailRegEx, "Please enter a valid email address"),
  }),
});

export const step1Schema = Yup.object({
  application: Yup.object({
    name: Yup.string()
      .required("Required")
      .min(4, "It must be greater than 4 character"),
    streetAddress: Yup.string()
      .required("Required")
      .min(4, "It must be greater than 4 character"),
    city: Yup.string()
      .required("Required")
      .min(2, "It must be greater than 2 character"),
    state: Yup.string()
      .required("Required")
      .oneOf(validStateOptions, "Invalid option"),
    zipCode: Yup.string()
      .required("Required")
      .matches(zipCodeRegEx, "It must be 5 digits")
      .max(5, "It must be 5 digits")
      .min(5, "It must be 5 digits"),
    homePhone: Yup.string().matches(phoneRegEx, {
      excludeEmptyString: true,
      message: "Please enter a valid phone number",
    }),
    workPhone: Yup.string().required("Required").matches(phoneRegEx, {
      excludeEmptyString: true,
      message: "Please enter a valid phone number",
    }),
    otherPhone: Yup.string().required("Required").matches(phoneRegEx, {
      excludeEmptyString: true,
      message: "Please enter a valid phone number",
    }),
    dob: Yup.string().required("Required"),
    lastFourSocial: Yup.string()
      .required("Required")
      .matches(/^\d{4}([-]|\s*)?(\d{4})?$/gm, "It must be 4 digits")
      .max(4, "It must be 4 digits")
      .min(4, "It must be 4 digits"),
    gender: Yup.string().required("Required"),
    maritalStatus: Yup.string().required("Required"),
    disabled: Yup.string().required("Required"),
    cityEmployee: Yup.string().required("Required"),
    races: Yup.array().min(1, "Required").typeError("Required"),
    incomeSources: Yup.object({
      wages: Yup.number()
        .typeError("Must be a number")
        .min(0, "Must be a positive number or 0")
        .transform((val) => (isNaN(val) ? 0 : val))
        .required("Required"),
      pension: Yup.number()
        .typeError("Must be a number")
        .min(0, "Must be a positive number or 0")
        .transform((val) => (isNaN(val) ? 0 : val))
        .required("Required"),
      publicAssistance: Yup.number()
        .typeError("Must be a number")
        .min(0, "Must be a positive number or 0")
        .transform((val) => (isNaN(val) ? 0 : val))
        .required("Required"),
      ssi: Yup.number()
        .typeError("Must be a number")
        .min(0, "Must be a positive number or 0")
        .transform((val) => (isNaN(val) ? 0 : val))
        .required("Required"),
      other: Yup.number()
        .typeError("Must be a number")
        .min(0, "Must be a positive number or 0")
        .transform((val) => (isNaN(val) ? 0 : val))
        .required("Required"),
    }),
  }),
});

export const step2Schema = Yup.object({
  householdIncome: Yup.array(),
});

export const step3Schema = Yup.object({
  propertyInformation: Yup.object().shape({
    streetAddress: Yup.string().test({
      message: "Required",
      test: (val, ctx) => {
        const { from } = ctx as Yup.TestContext & TestContextExtended;
        return from[1] &&
          from[1].value?.userDocumentInfo.length === 1 &&
          from[1].value?.userDocumentInfo[0] === "hasLetter"
          ? true
          : val
          ? true
          : false;
      },
    }),
    zipCode: Yup.string()
      .notRequired()
      .matches(zipCodeRegEx, "It must be 5 digits")
      .nullable()
      .transform((val) => (!!val ? val : null))
      .test({
        message: "Required",
        test: (val, ctx) => {
          const { from } = ctx as Yup.TestContext & TestContextExtended;
          return from[1] &&
            from[1].value?.userDocumentInfo.length === 1 &&
            from[1].value?.userDocumentInfo[0] === "hasLetter"
            ? true
            : val
            ? true
            : false;
        },
      }),
    housePrice: Yup.number()
      .typeError("Amount is required")
      .positive("It must be greater than 0")
      .integer("It must be an integer")
      .transform((_, val) => (val ? Number(val) : undefined))
      .test({
        message: "Required",
        test: (val, ctx) => {
          const { from } = ctx as Yup.TestContext & TestContextExtended;
          return from[1] &&
            from[1].value?.userDocumentInfo.length === 1 &&
            from[1].value?.userDocumentInfo[0] === "hasLetter"
            ? true
            : val
            ? true
            : false;
        },
      }),
    buyersAgentName: Yup.string().test({
      message: "Required",
      test: (val, ctx) => {
        const { from } = ctx as Yup.TestContext & TestContextExtended;
        return from[1] &&
          from[1].value?.userDocumentInfo.length === 1 &&
          from[1].value?.userDocumentInfo[0] === "hasLetter"
          ? true
          : val
          ? true
          : false;
      },
    }),
    buyersWorkPhone: Yup.string()
      .matches(phoneRegEx, {
        excludeEmptyString: true,
        message: "Please enter a valid phone number",
      })
      .test({
        message: "Required",
        test: (val, ctx) => {
          const { from } = ctx as Yup.TestContext & TestContextExtended;
          return from[1] &&
            from[1].value?.userDocumentInfo.length === 1 &&
            from[1].value?.userDocumentInfo[0] === "hasLetter"
            ? true
            : val
            ? true
            : false;
        },
      }),
    buyersAgentEmail: Yup.string()
      .matches(emailRegEx, {
        excludeEmptyString: true,
        message: "Please enter a valid email address",
      })
      .test({
        message: "Required",
        test: (val, ctx) => {
          const { from } = ctx as Yup.TestContext & TestContextExtended;
          return from[1] &&
            from[1].value?.userDocumentInfo.length === 1 &&
            from[1].value?.userDocumentInfo[0] === "hasLetter"
            ? true
            : val
            ? true
            : false;
        },
      }),
    sellersName: Yup.string().test({
      message: "Required",
      test: (val, ctx) => {
        const { from } = ctx as Yup.TestContext & TestContextExtended;
        return from[1] &&
          from[1].value?.userDocumentInfo.length === 1 &&
          from[1].value?.userDocumentInfo[0] === "hasLetter"
          ? true
          : val
          ? true
          : false;
      },
    }),
    sellersAgentName: Yup.string().test({
      message: "Required",
      test: (val, ctx) => {
        const { from } = ctx as Yup.TestContext & TestContextExtended;
        return from[1] &&
          from[1].value?.userDocumentInfo.length === 1 &&
          from[1].value?.userDocumentInfo[0] === "hasLetter"
          ? true
          : val
          ? true
          : false;
      },
    }),
    sellersWorkPhone: Yup.string()
      .matches(phoneRegEx, {
        excludeEmptyString: true,
        message: "Please enter a valid phone number",
      })
      .test({
        message: "Required",
        test: (val, ctx) => {
          const { from } = ctx as Yup.TestContext & TestContextExtended;
          return from[1] &&
            from[1].value?.userDocumentInfo.length === 1 &&
            from[1].value?.userDocumentInfo[0] === "hasLetter"
            ? true
            : val
            ? true
            : false;
        },
      }),
    sellersAgentEmail: Yup.string()
      .matches(emailRegEx, {
        excludeEmptyString: true,
        message: "Please enter a valid email address",
      })
      .test({
        message: "Required",
        test: (val, ctx) => {
          const { from } = ctx as Yup.TestContext & TestContextExtended;
          return from[1] &&
            from[1].value?.userDocumentInfo.length === 1 &&
            from[1].value?.userDocumentInfo[0] === "hasLetter"
            ? true
            : val
            ? true
            : false;
        },
      }),
  }),
});

export const step4Schema = Yup.object({
  attachments: Yup.object().shape({
    approvalLetter: Yup.object({
      selectedFiles: Yup.array().test({
        message: "Required",
        test: (val, ctx) => {
          const { from } = ctx as Yup.TestContext & TestContextExtended;
          return from[2] &&
            from[2].value?.userDocumentInfo.length === 1 &&
            (from[2].value?.userDocumentInfo[0] === "hasContract" ||
              from[2].value?.userDocumentInfo[0] === "hasNothing")
            ? true
            : !Array.isArray(val) || !val.length
            ? false
            : true;
        },
      }),
    }),
    taxReturn: Yup.object({
      selectedFiles: Yup.array().test({
        message: "Required",
        test: (val, ctx) => {
          const { from } = ctx as Yup.TestContext & TestContextExtended;
          return from[2] &&
            from[2].value?.userDocumentInfo.length === 1 &&
            from[2].value?.userDocumentInfo[0] === "hasNothing"
            ? true
            : !Array.isArray(val) || !val.length
            ? false
            : true;
        },
      }),
    }),
    payStubs: Yup.object({
      selectedFiles: Yup.array().min(1, "Required"),
    }),
    bankStatements: Yup.object({
      selectedFiles: Yup.array().min(1, "Required"),
    }),
  }),
});

export const fakeSchema = Yup.object();

export const BDPAValidationSchema = {
  userDocumentInfoSchema,
  defaultStepsSchema,
  step1Schema,
  step2Schema,
  step3Schema,
  step4Schema,
};
